import React from "react";
import { Button as AntdButton } from "antd";
import styled, { css } from "styled-components";

const buttonTypeMixinMap = {
  primary: css`
    font-weight: bold !important;
  `,
  secondary: css`
    font-size: medium;
    :focus,
    :hover {
      font-weight: bold !important;
    }
  `,
  tertiary: css`
    font-size: medium;
  `,
  quaternary: "",
  bordered: css`
    font-weight: bold !important;
    box-shadow: none !important;
  `
};

const buttonSizeMixinMap = {
  xl: css`
    font-size: 13px !important;
    height: 48px !important;
    padding: 14px 32px !important;
    min-width: 160px;
    span {
      height: 13px !important;
      line-height: 13px !important;
    }
  `,
  lg: css`
    font-size: 12px !important;
    height: 40px !important;
    padding: 10px 24px 7px 24px !important;
    min-width: 120px;
    span {
      height: 12px !important;
      line-height: 12px !important;
    }
  `,
  md: css`
    font-size: 11px !important;
    height: 32px !important;
    padding: 8px 24px !important;
    span {
      height: 11px !important;
      line-height: 11px !important;
    }
  `,
  sm: css`
    font-size: 10px !important;
    height: 24px !important;
    padding: 5px 16px 4px 16px !important;
    span {
      height: 10px !important;
      line-height: 10px !important;
    }
  `,
  xs: css`
    font-size: 9px !important;
    height: 16px !important;
    padding: 2px 8px !important;
    span {
      height: 9px !important;
      line-height: 9px !important;
    }
  `
};

const buttonColorMappers = (disabled, outline = false) => ({
  primary: {
    background: disabled ? "gray-40" : "primary",
    border: disabled ? "gray-40" : outline ? "white" : "primary",
    color: disabled ? "gray-20" : "white",
    hover: {
      background: "primary-hover",
      border: outline ? "white" : "primary-hover",
      color: "white"
    }
  },
  secondary: {
    background: disabled ? "gray-50" : "gray-100",
    border: disabled ? "gray-50" : "gray-100",
    color: disabled ? "gray-40" : "white",
    hover: {
      background: "gray-80",
      border: "gray-100",
      color: "white"
    }
  },
  tertiary: {
    background: "white",
    border: disabled ? "gray-40" : "primary",
    color: disabled ? "gray-40" : "primary",
    hover: {
      background: "white",
      border: "primary-light",
      color: "primary-hover"
    }
  },
  quaternary: {
    background: "white",
    border: disabled ? "gray-30" : "gray-40",
    color: disabled ? "gray-40" : "gray-60",
    hover: {
      background: "white",
      border: "gray-50",
      color: disabled ? "gray-40" : "gray-80"
    }
  },
  bordered: {
    background: "white",
    border: "white",
    color: "primary",
    hover: {
      background: "white",
      border: "white",
      color: "primary"
    }
  },
  alert: {
    background: "white",
    border: disabled ? "gray-40" : "alert",
    color: disabled ? "gray-40" : "alert",
    hover: {
      background: "white",
      border: "alert-hover",
      color: "alert-hover"
    }
  },
  critical: {
    background: disabled ? "gray-40" : "alert",
    border: disabled ? "gray-40" : "alert",
    color: disabled ? "gray-20" : "white",
    hover: {
      background: "alert-hover",
      border: "alert-hover",
      color: "white"
    }
  }
});

const buttonMixin = css`
  background-color: ${({ disabled, theme, type }) =>
    theme[
      `@${buttonColorMappers(disabled)[type] &&
        buttonColorMappers(disabled)[type].background}`
    ]} !important;
  border: 1px solid
    ${({ disabled, outline, theme, type }) =>
      theme[
        `@${buttonColorMappers(disabled, outline)[type] &&
          buttonColorMappers(disabled, outline)[type].border}`
      ]} !important;
  color: ${({ disabled, theme, type }) =>
    theme[
      `@${buttonColorMappers(disabled)[type] &&
        buttonColorMappers(disabled)[type].color}`
    ]} !important;
  :focus,
  :hover {
    ${({ disabled, theme, type }) =>
      !disabled
        ? css`
            background-color: ${theme[
              `@${buttonColorMappers(disabled)[type] &&
                buttonColorMappers(disabled)[type].hover.background}`
            ]} !important;
            border-color: ${theme[
              `@${buttonColorMappers(disabled)[type] &&
                buttonColorMappers(disabled)[type].hover.border}`
            ]} !important;
            color: ${theme[
              `@${buttonColorMappers(disabled)[type] &&
                buttonColorMappers(disabled)[type].hover.color}`
            ]} !important;
          `
        : ""}
  }
  min-width: 80px;
  ${({ type }) => buttonTypeMixinMap[type]}
  ${({ _size }) =>
    buttonSizeMixinMap[
      _size
    ]}
  transition: background-color 0.4s ease, border-color 0.4s ease, color 0.4s ease, font-weight 0.4s ease !important;
`;

const StyledButton = styled(AntdButton)`
  ${buttonMixin}
`;

function Button({
  children,
  type = "tertiary",
  size = "md",
  outline = false,
  ...props
}) {
  return (
    <StyledButton
      outline={outline ? 1 : undefined}
      _size={size}
      size={size in buttonSizeMixinMap ? undefined : size}
      type={type}
      {...props}
    >
      {children}
    </StyledButton>
  );
}

export default Button;
